import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaTimes, FaImage, FaPlus, FaEdit, FaTrash, FaLink, FaCalendar, FaExpand } from 'react-icons/fa';
import { TbDragDrop } from 'react-icons/tb';
import CommonModal from './CommonModal';
import { commonModalSetting, changeState, addZero, catchError } from 'JS/common';
import { uploadPopupImage, createPopup, editPopup, deletePopup } from 'JS/API';
import unwrapResponse from 'utils/unwrapResponse';
import { validatePopupForm, readImageSize } from 'utils/popupForm';
import { designTokens, commonStyles } from 'utils/designSystem';
import { detailModalStyles } from 'utils/modalDesignSystem';

const DAY_SECONDS = 24 * 60 * 60;
const IMAGE_SIZE_LIMIT_KB = 500;

const formatKb = bytes => `${Math.round(bytes / 1024).toLocaleString()}KB`;

const hover = (base, hoverColor) => ({
  onMouseEnter: e => (e.currentTarget.style.backgroundColor = hoverColor),
  onMouseLeave: e => (e.currentTarget.style.backgroundColor = base),
});
const PERIOD_PRESETS = [
  { label: '1주', days: 7 },
  { label: '2주', days: 14 },
  { label: '1개월', days: 30 },
  { label: '3개월', days: 90 },
];

const toLocalInput = date =>
  `${date.getFullYear()}-${addZero(date.getMonth() + 1)}-${addZero(date.getDate())}T${addZero(
    date.getHours()
  )}:${addZero(date.getMinutes())}`;

const toEpoch = localInput => Math.floor(new Date(localInput).getTime() / 1000);

const fromEpoch = epoch => toLocalInput(new Date(epoch * 1000));

const addDays = (localInput, days) => {
  const date = new Date(localInput);
  date.setDate(date.getDate() + days);
  return toLocalInput(date);
};

const defaultPeriod = () => {
  const start = toLocalInput(new Date());
  return { start, end: addDays(start, 7) };
};

const describePeriod = (start, end) => {
  const seconds = toEpoch(end) - toEpoch(start);
  if (Number.isNaN(seconds)) return { text: '시작과 종료 일시를 모두 입력해 주세요.', invalid: true };
  if (seconds <= 0) return { text: '종료 일시가 시작 일시보다 앞입니다.', invalid: true };
  const days = Math.floor(seconds / DAY_SECONDS);
  const hours = Math.floor((seconds % DAY_SECONDS) / 3600);
  const parts = [];
  if (days) parts.push(`${days}일`);
  if (hours) parts.push(`${hours}시간`);
  return { text: `게시 기간 ${parts.join(' ') || '1시간 미만'}`, invalid: false };
};

const styles = {
  overlay: detailModalStyles.overlay,
  container: {
    backgroundColor: designTokens.colors.surface,
    borderRadius: designTokens.borderRadius.xl,
    boxShadow: designTokens.shadows.lg,
    width: '100%',
    maxWidth: '640px',
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  header: detailModalStyles.header,
  title: detailModalStyles.title,
  closeButton: detailModalStyles.closeButton,
  content: detailModalStyles.content,
  section: { marginBottom: designTokens.spacing.xl },
  label: {
    display: 'flex',
    alignItems: 'center',
    gap: designTokens.spacing.sm,
    fontSize: '14px',
    fontWeight: '600',
    color: designTokens.colors.text.primary,
    marginBottom: designTokens.spacing.md,
  },
  required: { color: designTokens.colors.danger },
  input: commonStyles.input,
  sizeInput: { ...commonStyles.input, flex: 1 },
  helpText: {
    fontSize: '12px',
    color: designTokens.colors.text.tertiary,
    marginTop: designTokens.spacing.xs,
  },
  dropZone: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: designTokens.spacing.sm,
    minHeight: '180px',
    border: `2px dashed ${designTokens.colors.border}`,
    borderRadius: designTokens.borderRadius.lg,
    backgroundColor: designTokens.colors.background,
    color: designTokens.colors.text.tertiary,
    cursor: 'pointer',
    fontSize: '13px',
  },
  dropZoneActive: {
    borderColor: designTokens.colors.primary,
    backgroundColor: '#eff6ff',
    color: designTokens.colors.primary,
  },
  preview: {
    display: 'flex',
    gap: designTokens.spacing.lg,
    alignItems: 'flex-start',
    padding: designTokens.spacing.md,
    border: `1px solid ${designTokens.colors.borderLight}`,
    borderRadius: designTokens.borderRadius.lg,
    backgroundColor: designTokens.colors.background,
  },
  previewImage: {
    width: '160px',
    maxHeight: '220px',
    objectFit: 'contain',
    borderRadius: designTokens.borderRadius.md,
    backgroundColor: designTokens.colors.surface,
    border: `1px solid ${designTokens.colors.borderLight}`,
  },
  previewMeta: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: designTokens.spacing.xs,
    fontSize: '13px',
    color: designTokens.colors.text.secondary,
    wordBreak: 'break-all',
  },
  inlineRow: { display: 'flex', alignItems: 'center', gap: designTokens.spacing.md },
  sizeSeparator: { color: designTokens.colors.text.tertiary, fontWeight: '500' },
  periodRow: {
    display: 'grid',
    gridTemplateColumns: '48px 1fr',
    alignItems: 'center',
    gap: designTokens.spacing.md,
    marginBottom: designTokens.spacing.sm,
  },
  periodLabel: { fontSize: '13px', color: designTokens.colors.text.secondary, fontWeight: '500' },
  presetRow: { display: 'flex', alignItems: 'center', gap: designTokens.spacing.sm, flexWrap: 'wrap' },
  presetButton: {
    ...commonStyles.buttonBase,
    padding: `${designTokens.spacing.xs} ${designTokens.spacing.md}`,
    fontSize: '12px',
    backgroundColor: designTokens.colors.surface,
    color: designTokens.colors.text.secondary,
    border: `1px solid ${designTokens.colors.border}`,
  },
  actionButtons: { ...detailModalStyles.actionButtons, justifyContent: 'space-between' },
  actionGroup: { display: 'flex', gap: designTokens.spacing.md },
  primaryButton: detailModalStyles.primaryButton,
  secondaryButton: detailModalStyles.secondaryButton,
  dangerButton: {
    ...detailModalStyles.secondaryButton,
    color: designTokens.colors.danger,
    borderColor: '#fecaca',
  },
  smallButton: {
    ...commonStyles.buttonBase,
    padding: `${designTokens.spacing.xs} ${designTokens.spacing.md}`,
    fontSize: '12px',
    backgroundColor: designTokens.colors.surface,
    color: designTokens.colors.text.secondary,
    border: `1px solid ${designTokens.colors.border}`,
    alignSelf: 'flex-start',
  },
};

const PopupApplyModal = ({ setModal, mode, info, setInfo }) => {
  const [alert, setAlert] = useState('');
  const [alertBox, setAlertBox] = useState({ mode: '', context: '', bool: false });
  const [submitting, setSubmitting] = useState(false);
  const [uploadImg, setUploadImg] = useState('');
  const [fileName, setFileName] = useState('');
  const [fileSize, setFileSize] = useState(0);
  const [naturalSize, setNaturalSize] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [period, setPeriod] = useState(() =>
    mode === 'edit' && info?.start_date && info?.end_date
      ? { start: fromEpoch(info.start_date), end: fromEpoch(info.end_date) }
      : defaultPeriod()
  );
  const navigate = useNavigate();
  const isEdit = mode === 'edit';
  const periodInfo = describePeriod(period.start, period.end);

  const buildPayload = img_url => ({
    img_url: img_url || info?.img_url,
    link_url: info?.link_url ? info.link_url : null,
    width: Number(info?.width),
    height: Number(info?.height),
    start_date: info?.start_date,
    end_date: info?.end_date,
  });

  //= 팝업 등록
  const newPopup = async img_url => {
    const result = await createPopup(buildPayload(img_url));
    if (typeof result === 'object') {
      setAlert('completeApply');
      commonModalSetting(setAlertBox, true, 'alert', '등록이 완료되었습니다.');
    } else return await catchError(result, navigate, setAlertBox, setAlert);
  };

  //= 팝업 수정
  const modifyPopup = async img_url => {
    const result = await editPopup(info?.id, buildPayload(img_url));
    if (typeof result === 'object') {
      setAlert('completeEdit');
      commonModalSetting(setAlertBox, true, 'alert', '수정이 완료되었습니다.');
    } else return await catchError(result, navigate, setAlertBox, setAlert);
  };

  //= 이미지 선택 (파일 input, 드래그앤드롭 공용)
  const selectImage = async file => {
    if (!file) return;
    if (!file.type.startsWith('image/'))
      return commonModalSetting(setAlertBox, true, 'alert', '이미지 파일만 업로드할 수 있습니다.');
    const reader = new FileReader();
    reader.onload = () => setUploadImg(reader.result);
    reader.readAsDataURL(file);
    setFileName(file.name);
    setFileSize(file.size);
    changeState(setInfo, 'img', file);
    const size = await readImageSize(file);
    if (!size) return;
    setNaturalSize(size);
    setInfo(prev => ({ ...prev, width: size.width, height: size.height }));
  };

  const onDrop = e => {
    e.preventDefault();
    setDragging(false);
    const files = e.dataTransfer?.files;
    if (!files?.length) return;
    if (files.length > 1)
      return commonModalSetting(setAlertBox, true, 'alert', '한 장만 업로드하실 수 있습니다.');
    selectImage(files[0]);
  };

  //= 검증 후 업로드 → 등록/수정
  const submit = async () => {
    if (submitting) return;
    setAlert('');
    const message = validatePopupForm({
      hasImage: Boolean(info?.img || info?.img_url),
      width: info?.width,
      height: info?.height,
      start_date: info?.start_date,
      end_date: info?.end_date,
    });
    if (message) return commonModalSetting(setAlertBox, true, 'alert', message);
    setSubmitting(true);
    try {
      if (!info?.img) return await modifyPopup();
      const result = await uploadPopupImage(info.img);
      if (typeof result !== 'object') return await catchError(result, navigate, setAlertBox, setAlert);
      const url = unwrapResponse(result)?.url;
      if (!url) return commonModalSetting(setAlertBox, true, 'alert', '이미지 업로드에 실패했습니다.');
      if (isEdit) await modifyPopup(url);
      else await newPopup(url);
    } finally {
      setSubmitting(false);
    }
  };

  //= 팝업 삭제
  const removePopup = async () => {
    setAlert('');
    const result = await deletePopup([info?.id]);
    if (typeof result === 'object') {
      setAlert('completeDelete');
      commonModalSetting(setAlertBox, true, 'alert', '삭제가 완료되었습니다.');
    } else return await catchError(result, navigate, setAlertBox, setAlert);
  };

  const confirmDelete = () => {
    setAlert('confirmDelete');
    commonModalSetting(
      setAlertBox,
      true,
      'confirm',
      '팝업을 삭제하시겠습니까?<br/>삭제된 팝업은 복구할 수 없습니다.'
    );
  };

  const applyPreset = days =>
    setPeriod(prev => (Number.isNaN(toEpoch(prev.start)) ? prev : { ...prev, end: addDays(prev.start, days) }));

  useEffect(() => {
    setInfo(prev => ({ ...prev, start_date: toEpoch(period.start), end_date: toEpoch(period.end) }));
  }, [period, setInfo]);

  useEffect(() => {
    if (isEdit) setUploadImg(info?.img_url);
  }, [isEdit, info?.img_url]);

  useEffect(() => {
    const onKeyDown = e => {
      if (e.key === 'Escape' && !alertBox.bool) setModal(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [alertBox.bool, setModal]);

  return (
    <>
      <div style={styles.overlay} onDragOver={e => e.preventDefault()} onDrop={e => e.preventDefault()}>
        <div style={styles.container}>
          <div style={styles.header}>
            <h3 style={styles.title}>
              <FaImage size={16} />
              {isEdit ? '팝업 수정' : '팝업 등록'}
            </h3>
            <button
              style={styles.closeButton}
              onClick={() => setModal(false)}
              title='닫기'
              {...hover('transparent', designTokens.colors.background)}>
              <FaTimes />
            </button>
          </div>

          <div style={styles.content}>
            <input
              type='file'
              id='popupImageInput'
              accept='image/*'
              style={{ display: 'none' }}
              onChange={e => {
                selectImage(e.target.files?.[0]);
                e.target.value = '';
              }}
            />

            <div style={styles.section}>
              <label style={styles.label}>
                <FaImage size={12} />
                이미지 <span style={styles.required}>*</span>
              </label>
              {uploadImg ? (
                <div style={styles.preview}>
                  <img src={uploadImg} alt='팝업 미리보기' style={styles.previewImage} />
                  <div style={styles.previewMeta}>
                    {fileName ? <span>{fileName}</span> : <span>등록된 이미지</span>}
                    {fileSize ? (
                      <span style={fileSize > IMAGE_SIZE_LIMIT_KB * 1024 ? { color: designTokens.colors.danger, fontWeight: '600' } : undefined}>
                        용량 {formatKb(fileSize)}
                        {fileSize > IMAGE_SIZE_LIMIT_KB * 1024 ? ' · 용량이 커서 서비스 화면이 느려질 수 있습니다' : ''}
                      </span>
                    ) : null}
                    {naturalSize ? (
                      <span>
                        원본 {naturalSize.width} × {naturalSize.height}px
                      </span>
                    ) : null}
                    <label
                      htmlFor='popupImageInput'
                      style={styles.smallButton}
                      {...hover(designTokens.colors.surface, designTokens.colors.background)}>
                      이미지 변경
                    </label>
                  </div>
                </div>
              ) : (
                <label
                  htmlFor='popupImageInput'
                  style={{ ...styles.dropZone, ...(dragging ? styles.dropZoneActive : {}) }}
                  onDragOver={e => e.preventDefault()}
                  onDragEnter={() => setDragging(true)}
                  onDragLeave={() => setDragging(false)}
                  onDrop={onDrop}>
                  <TbDragDrop size={40} />
                  <span>이미지를 끌어다 놓거나 클릭해서 선택하세요</span>
                  <span>jpg · png · webp · gif</span>
                </label>
              )}
              <div style={styles.helpText}>
                용량이 큰 이미지는 사용자 화면에서 팝업이 늦게 뜹니다. {IMAGE_SIZE_LIMIT_KB}KB 이하로 줄여서 올려 주세요.
              </div>
            </div>

            <div style={styles.section}>
              <label style={styles.label}>
                <FaLink size={12} />
                링크
              </label>
              <input
                type='text'
                style={styles.input}
                placeholder='https://'
                value={info?.link_url ?? ''}
                onChange={e => changeState(setInfo, 'link_url', e.target.value)}
              />
              <div style={styles.helpText}>비워두면 이미지를 클릭해도 이동하지 않습니다.</div>
            </div>

            <div style={styles.section}>
              <label style={styles.label}>
                <FaExpand size={12} />
                팝업 크기(px) <span style={styles.required}>*</span>
              </label>
              <div style={styles.inlineRow}>
                <input
                  type='number'
                  min='1'
                  style={styles.sizeInput}
                  placeholder='가로'
                  value={info?.width ?? ''}
                  onChange={e => changeState(setInfo, 'width', e.target.value)}
                />
                <span style={styles.sizeSeparator}>×</span>
                <input
                  type='number'
                  min='1'
                  style={styles.sizeInput}
                  placeholder='세로'
                  value={info?.height ?? ''}
                  onChange={e => changeState(setInfo, 'height', e.target.value)}
                />
              </div>
              <div style={styles.helpText}>이미지를 선택하면 원본 크기로 채워집니다. 서비스에 표시될 크기입니다.</div>
            </div>

            <div style={styles.section}>
              <label style={styles.label}>
                <FaCalendar size={12} />
                게시 기간 <span style={styles.required}>*</span>
              </label>
              <div style={styles.periodRow}>
                <span style={styles.periodLabel}>시작</span>
                <input
                  type='datetime-local'
                  style={styles.input}
                  value={period.start}
                  onChange={e => changeState(setPeriod, 'start', e.target.value)}
                />
              </div>
              <div style={styles.periodRow}>
                <span style={styles.periodLabel}>종료</span>
                <input
                  type='datetime-local'
                  style={styles.input}
                  min={period.start}
                  value={period.end}
                  onChange={e => changeState(setPeriod, 'end', e.target.value)}
                />
              </div>
              <div style={styles.periodRow}>
                <span style={styles.periodLabel}>기간</span>
                <div style={styles.presetRow}>
                  {PERIOD_PRESETS.map(({ label, days }) => (
                    <button
                      key={label}
                      type='button'
                      style={styles.presetButton}
                      onClick={() => applyPreset(days)}
                      {...hover(designTokens.colors.surface, designTokens.colors.background)}>
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              <div
                style={{
                  ...styles.helpText,
                  color: periodInfo.invalid ? designTokens.colors.danger : designTokens.colors.text.tertiary,
                }}>
                {periodInfo.text} · 기간이 겹치면 가장 최근에 등록한 팝업 1개만 서비스에 노출됩니다.
              </div>
            </div>
          </div>

          <div style={styles.actionButtons}>
            <div style={styles.actionGroup}>
              {isEdit && (
                <button style={styles.dangerButton} onClick={confirmDelete} {...hover(designTokens.colors.surface, '#fef2f2')}>
                  <FaTrash size={12} />
                  삭제
                </button>
              )}
            </div>
            <div style={styles.actionGroup}>
              <button
                style={styles.secondaryButton}
                onClick={() => setModal(false)}
                {...hover(designTokens.colors.surface, designTokens.colors.background)}>
                취소
              </button>
              <button
                style={{ ...styles.primaryButton, opacity: submitting ? 0.6 : 1, cursor: submitting ? 'not-allowed' : 'pointer' }}
                onClick={submit}
                disabled={submitting}
                {...hover(designTokens.colors.primary, designTokens.colors.primaryHover)}>
                {isEdit ? <FaEdit size={12} /> : <FaPlus size={12} />}
                {isEdit ? '수정' : '등록'}
              </button>
            </div>
          </div>
        </div>
      </div>
      {alertBox.bool && (
        <CommonModal
          setModal={setAlertBox}
          modal={alertBox}
          okFn={() => {
            if (alert === 'completeApply' || alert === 'completeEdit' || alert === 'completeDelete')
              setModal(false);
            else if (alert === 'confirmDelete') removePopup();
            else if (alert === 'logout') navigate('/');
            else return;
          }}
        />
      )}
    </>
  );
};

export default PopupApplyModal;
